import { faPlus, faTimesCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    clearSurchargeQuote,
    createPaymentMethodSessionThunk,
    fetchPaymentMethodsThunk,
    fetchSurchargeQuoteThunk,
    payWithTokenThunk,
    resetTokenPayment
} from 'actions/paymentMethods/PaymentMethodsActions';
import { trackDepositBookingCompletedThunk } from 'actions/onlineBooking/actions/OnlineBookingBookingActions';
import { applyPendingPromocodeThunk } from 'actions/promocodes/OnlineBookingPromocodeActions';
import {
    navigateToStep,
    setOnlineBookingCheckoutView
} from 'actions/onlineBooking/actions/OnlineBookingFlowActions';
import { AdyenAddCardDropIn } from 'components/AdyenDropIn/AdyenAddCardDropIn';
import { LeftColumn } from 'components/Layout/TwoColumns/LeftColumn/LeftColumn';
import { RightColumn } from 'components/Layout/TwoColumns/RightColumn/RightColumn';
import { TwoColumnsLayout } from 'components/Layout/TwoColumns/TwoColumnsLayout';
import { MarketplaceInfo } from 'components/Marketplace/Info/MarketplaceInfo';
import { OnlineBookingHeader } from 'components/OnlineBooking/Header/OnlineBookingHeader';
import { OnlineBookingResume } from 'components/OnlineBooking/Resume/OnlineBookingResume';
import { Alert } from 'components/UI/Alert/Alert';
import { Button } from 'components/UI/Button/Button';
import { SelectableCreditCard } from 'components/UI/SelectableCreditCard/SelectableCreditCard';
import { Spinner } from 'components/UI/Spinner/Spinner';
import { usePhoneFormat } from 'hooks/usePhoneFormat';
import React from 'react';
import { useSelector } from 'react-redux';
import { getMarketplace } from 'reducers/marketplace/MarketplaceSelectors';
import {
    getOnlineBookingCustomer,
    getOnlineBookingDepositRequired
} from 'reducers/onlineBooking/OnlineBookingSelectors';
import {
    getAdyenSession,
    getPaymentMethodsFetched,
    getPaymentMethodsLoading,
    getSessionLoading,
    getSortedPaymentMethods,
    getTokenPaymentStatus
} from 'reducers/paymentMethods/PaymentMethodsSelectors';
import { TokenPaymentStatus } from 'reducers/paymentMethods/PaymentMethodsState';
import { dispatch, RootState } from 'store';
import { OnlineBookingStep } from 'types/OnlineBooking';
import { PaymentMethod } from 'types/PaymentMethod';
import { hasValidCardOnFile } from 'utils/paymentMethodUtils';

export const OnlineBookingCheckoutStep: React.FC = () => {
    const marketplace = useSelector(getMarketplace);
    const customer = useSelector(getOnlineBookingCustomer);
    const checkoutView = useSelector((state: RootState) => state.onlineBooking.checkoutView);
    const depositInvoice = useSelector((state: RootState) => state.invoices.depositInvoice);

    const paymentMethods = useSelector(getSortedPaymentMethods);
    const paymentMethodsFetched = useSelector(getPaymentMethodsFetched);
    const paymentMethodsLoading = useSelector(getPaymentMethodsLoading);
    const adyenSession = useSelector(getAdyenSession);
    const sessionLoading = useSelector(getSessionLoading);
    const tokenPaymentStatus = useSelector(getTokenPaymentStatus);

    const formattedPhone = usePhoneFormat(marketplace.phone ?? '');

    const isDepositRequired = useSelector(getOnlineBookingDepositRequired);
    const hasValidCard = hasValidCardOnFile(paymentMethods);

    const [selectedCard, setSelectedCard] = React.useState<PaymentMethod | null>(null);
    const [hasError, setHasError] = React.useState(false);

    // The amount to charge now is the unpaid deposit, not the invoice grand
    // total. `depositInvoice.total` is the full invoice (service minus
    // discounts); the deposit lives in `deposits.unpaidTotal`.
    const depositAmount = React.useMemo(() => {
        const raw = depositInvoice?.deposits?.unpaidTotal;
        if (!raw) return 0;
        const parsed = Number(raw);
        return Number.isFinite(parsed) ? parsed : 0;
    }, [depositInvoice?.deposits?.unpaidTotal]);

    // The server decides what is actually owed and may collect nothing at all —
    // a category that carries no rule, or a customer who stopped being new
    // between lookup and booking. An arrived invoice with no unpaid deposit
    // means "continue without charging", not a failure.
    const nothingToCollect = isDepositRequired && !!depositInvoice && depositAmount <= 0;

    const isCardOnFileOnly =
        marketplace.settings.cardOnFileEnabled && (!isDepositRequired || nothingToCollect);

    React.useEffect(() => {
        if (nothingToCollect && !isCardOnFileOnly) {
            dispatch(navigateToStep(OnlineBookingStep.Success));
        }
    }, [nothingToCollect, isCardOnFileOnly]);

    React.useLayoutEffect(() => {
        dispatch(resetTokenPayment());
        dispatch(applyPendingPromocodeThunk()).catch(() => undefined);

        return () => {
            dispatch(clearSurchargeQuote());
            dispatch(resetTokenPayment());
        };
    }, []);

    React.useEffect(() => {
        if (customer.id) {
            dispatch(fetchPaymentMethodsThunk(customer.id));
        }
    }, [customer.id]);

    React.useEffect(() => {
        setHasError(false);
    }, [checkoutView]);

    React.useEffect(() => {
        if (paymentMethodsFetched && !selectedCard) {
            const primary = paymentMethods.find(card => card.primary && !card.expired);
            if (primary && customer.id && depositAmount > 0) {
                setSelectedCard(primary);
                dispatch(fetchSurchargeQuoteThunk(customer.id, primary.id, depositAmount));
            }
        }
    }, [paymentMethodsFetched, paymentMethods, selectedCard, customer.id, depositAmount]);

    React.useEffect(() => {
        if (isCardOnFileOnly) return;

        if (tokenPaymentStatus === TokenPaymentStatus.Success) {
            dispatch(trackDepositBookingCompletedThunk());
            dispatch(navigateToStep(OnlineBookingStep.Success));
        } else if (tokenPaymentStatus === TokenPaymentStatus.Error) {
            setHasError(true);
            dispatch(resetTokenPayment());
        }
    }, [isCardOnFileOnly, tokenPaymentStatus]);

    React.useEffect(() => {
        if (!isCardOnFileOnly || !paymentMethodsFetched || paymentMethodsLoading || !customer.id) {
            return;
        }

        if (hasValidCard) {
            dispatch(navigateToStep(OnlineBookingStep.Success));
            return;
        }

        if (checkoutView === 'cards_list') {
            dispatch(setOnlineBookingCheckoutView('add_card'));
            dispatch(createPaymentMethodSessionThunk(customer.id));
        }
    }, [
        isCardOnFileOnly,
        paymentMethodsFetched,
        paymentMethodsLoading,
        hasValidCard,
        customer.id,
        checkoutView
    ]);

    const handleAddCard = () => {
        if (!customer.id) return;
        setHasError(false);
        dispatch(setOnlineBookingCheckoutView('add_card'));
        dispatch(createPaymentMethodSessionThunk(customer.id));
    };

    const handleCardAdded = (success: boolean) => {
        if (!success || !customer.id) return;

        if (isCardOnFileOnly) {
            dispatch(navigateToStep(OnlineBookingStep.Success));
            return;
        }

        dispatch(setOnlineBookingCheckoutView('cards_list'));
        dispatch(fetchPaymentMethodsThunk(customer.id));
    };

    const handleCardAddError = () => {
        setHasError(true);
    };

    const handleSelectCard = (card: PaymentMethod) => {
        if (card.id === selectedCard?.id) return;
        setSelectedCard(card);
        setHasError(false);
        dispatch(clearSurchargeQuote());
        if (customer.id && depositAmount > 0) {
            dispatch(fetchSurchargeQuoteThunk(customer.id, card.id, depositAmount));
        }
    };

    const handlePay = () => {
        if (!selectedCard || !depositInvoice?.uuid || depositAmount <= 0) return;
        setHasError(false);
        dispatch(payWithTokenThunk(depositInvoice.uuid, selectedCard.uuid, depositAmount));
    };

    const handleBack = () => {
        if (checkoutView === 'add_card') {
            dispatch(setOnlineBookingCheckoutView('cards_list'));
        }
    };

    const hasCards = paymentMethods.length > 0;
    const isProcessing = tokenPaymentStatus === TokenPaymentStatus.Processing;
    const waitingForCardOnFileDecision =
        isCardOnFileOnly && (!paymentMethodsFetched || paymentMethodsLoading || hasValidCard);

    const showSpinner =
        (nothingToCollect && !isCardOnFileOnly) ||
        waitingForCardOnFileDecision ||
        (paymentMethodsLoading && !paymentMethodsFetched) ||
        (checkoutView === 'add_card' && sessionLoading && !adyenSession);
    const showEmptyState =
        paymentMethodsFetched &&
        !hasCards &&
        checkoutView === 'cards_list' &&
        !showSpinner &&
        !isCardOnFileOnly;
    const showCardsList = checkoutView === 'cards_list' && hasCards && !showSpinner;
    const showAddCardDropIn = checkoutView === 'add_card' && adyenSession && !sessionLoading;
    const showPayButton =
        checkoutView === 'cards_list' && hasCards && isDepositRequired && !nothingToCollect;

    const backHandler = isCardOnFileOnly
        ? undefined
        : checkoutView === 'add_card'
        ? handleBack
        : undefined;

    return (
        <TwoColumnsLayout header={<OnlineBookingHeader onBack={backHandler} />}>
            <LeftColumn contentClassName="flex flex-col flex-1">
                {!waitingForCardOnFileDecision && (
                    <>
                        <h1 className="h1 underlined w-full mb-6">Payment info</h1>

                        {isCardOnFileOnly && (
                            <p className="text-base w-full mb-6">
                                A valid credit or debit card is required to complete your booking.
                                Your card will not be charged at this time.
                            </p>
                        )}
                    </>
                )}

                {hasError && (
                    <Alert variant="error" icon={faTimesCircle} className="mb-5">
                        <b>Payment cannot be processed.</b> Please try a different card or contact{' '}
                        <b>{marketplace.name}</b> directly to request an appointment using a
                        different payment method. If you have any questions, contact{' '}
                        <b>{marketplace.name}</b> at{' '}
                        <a href={`tel:${marketplace.phone}`} className="no-underline">
                            {formattedPhone}
                        </a>
                        .
                    </Alert>
                )}

                {showSpinner && (
                    <div className="w-full text-center">
                        <Spinner size={50} />
                    </div>
                )}

                {showEmptyState && (
                    <>
                        <p className="text-base mb-6">
                            You don&apos;t have any saved cards yet. Add a card to make future
                            payments faster and easier.
                        </p>

                        <div className="text-left">
                            <Button variant="link" onClick={handleAddCard}>
                                <FontAwesomeIcon icon={faPlus} />
                                <span className="ml-2">Add new card</span>
                            </Button>
                        </div>
                    </>
                )}

                {showAddCardDropIn && adyenSession && (
                    <AdyenAddCardDropIn
                        session={adyenSession}
                        onComplete={handleCardAdded}
                        onError={handleCardAddError}
                    />
                )}

                {hasError && checkoutView === 'add_card' && (
                    <div className="text-center mt-4">
                        <Button onClick={handleAddCard}>Retry</Button>
                    </div>
                )}

                {showCardsList && (
                    <>
                        {paymentMethods.map(card => (
                            <SelectableCreditCard
                                key={card.id}
                                card={card}
                                selected={selectedCard?.id === card.id}
                                onSelect={handleSelectCard}
                            />
                        ))}

                        <div className="text-left">
                            <Button variant="link" onClick={handleAddCard}>
                                <FontAwesomeIcon icon={faPlus} />
                                <span className="ml-2">Add new card</span>
                            </Button>
                        </div>
                    </>
                )}
            </LeftColumn>

            <RightColumn
                poweredByCuddles
                footer={
                    showPayButton ? (
                        <Button
                            fullWidth
                            loading={isProcessing}
                            disabled={!selectedCard || isProcessing}
                            onClick={handlePay}
                        >
                            Pay
                        </Button>
                    ) : undefined
                }
            >
                <div className="flex flex-col gap-5">
                    <MarketplaceInfo
                        inverted
                        marketplace={marketplace}
                        className="hide-on-mobile-and-tablet"
                    />

                    <OnlineBookingResume />
                </div>
            </RightColumn>
        </TwoColumnsLayout>
    );
};
